import React, { useState } from 'react';
import { Direction } from '../../state/ArenaTypes';
import { ArrowUp, ArrowDown, Check, Loader2 } from 'lucide-react';
import { arenaAudio } from '../../services/ArenaAudioService';
import { motion } from 'framer-motion';
import { AuctionTurnTimerBadge } from './AuctionTurnTimerBadge';

interface DeclareNoTrumpHudProps {
  winningBid: number;
  onSelectDirection: (direction: Direction) => void | Promise<void>;
  turnTimerSeconds?: number | null;
  turnTimerMax?: number;
}

interface DirectionOption {
  id: Direction;
  label: string;
}

const DIRECTIONS: DirectionOption[] = [
  { id: 'UP', label: 'Uptown' },
  { id: 'DOWN', label: 'Downtown' }
];

export const DeclareNoTrumpHud: React.FC<DeclareNoTrumpHudProps> = ({
  winningBid,
  onSelectDirection,
  turnTimerSeconds,
  turnTimerMax = 20
}) => {
  const [selectedDirection, setSelectedDirection] = useState<Direction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelectDirection = (dir: Direction) => {
    if (isSubmitting) return;
    arenaAudio.playCardSelect();
    setSelectedDirection(dir);
  };

  const handleDeclare = async () => {
    if (!selectedDirection || isSubmitting) return;
    setIsSubmitting(true);
    arenaAudio.playBidChime();
    try {
      await onSelectDirection(selectedDirection);
    } catch {
      setIsSubmitting(false);
    }
  };

  const selectedDirObj = DIRECTIONS.find(d => d.id === selectedDirection);

  const maxTimer = turnTimerMax && turnTimerMax > 0 ? turnTimerMax : 20;
  const redThreshold = Math.max(5, Math.round(maxTimer * 0.25));
  const warningThreshold = Math.max(10, Math.round(maxTimer * 0.5));

  const isCritical = turnTimerSeconds !== undefined && turnTimerSeconds !== null && turnTimerSeconds <= redThreshold;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 16, x: '-50%' }}
      animate={{ opacity: 1, scale: 1, y: 0, x: '-50%' }}
      exit={{
        opacity: 0,
        scale: 0.95,
        y: 12,
        x: '-50%',
        transition: { duration: 0.2, ease: [0.32, 0, 0.67, 0] }
      }}
      transition={{ type: 'spring', damping: 24, stiffness: 300, mass: 0.8 }}
      className="absolute bottom-[178px] sm:bottom-[198px] md:bottom-[218px] left-1/2 z-40 w-[calc(100%-36px)] max-w-[430px] sm:max-w-[455px] pointer-events-auto select-none rounded-[28px] bg-[#0a1120] transition-[border-color,box-shadow] duration-300 transform-gpu [backface-visibility:hidden] [webkit-backface-visibility:hidden] acs-font-scale-110"
      style={{
        border: isCritical ? '1.5px solid #f43f5e' : '1.5px solid #d48b28',
        boxShadow: isCritical
          ? '0 0 10px 1px rgba(244, 63, 94, 0.5), 0 16px 45px rgba(0, 0, 0, 0.85)'
          : '0 0 10px 1px rgba(229, 142, 25, 0.35), 0 16px 45px rgba(0, 0, 0, 0.85)',
        WebkitBackfaceVisibility: 'hidden',
        willChange: 'transform, opacity',
      }}
    >
      {/* CIRCULAR TIMER BADGE SITTING ON TOP OF THE BORDER (z-30 so border does NOT cut through it) */}
      <AuctionTurnTimerBadge turnTimerSeconds={turnTimerSeconds} turnTimerMax={turnTimerMax} className="absolute -top-7 left-1/2 -translate-x-1/2 z-30" />

      {/* Clean, crisp single card container */}
      <div className="w-full h-full rounded-[27px] px-2.5 sm:px-3 pt-8 sm:pt-9 pb-2 sm:pb-2.5 relative z-10 flex flex-col space-y-1.5 sm:space-y-2 bg-gradient-to-b from-[#0e1728] via-[#09101d] to-[#050912]">
        {/* Subtle Watermarks clipped in dedicated layer */}
        <div className="absolute inset-0 rounded-[27px] overflow-hidden pointer-events-none select-none">
          {/* Classic Spade Silhouette (Upper Left - Plump & Proportional matching reference) */}
          <svg
            className="absolute top-0.5 left-1 w-20 h-20 sm:w-24 sm:h-24 rotate-[24deg] text-slate-300/[0.08] select-none pointer-events-none"
            viewBox="0 0 100 100"
            fill="currentColor"
          >
            <path d="M 50 15 C 46 28 14 50 14 66 C 14 78 26 84 42 78 C 39 83 34 89 26 93 L 74 93 C 66 89 61 83 58 78 C 74 84 86 78 86 66 C 86 50 54 28 50 15 Z" />
          </svg>

          {/* Secondary Echo Spade (Below main spade matching reference image) */}
          <svg
            className="absolute top-11 left-10 sm:top-12 sm:left-12 w-9 h-9 sm:w-11 sm:h-11 rotate-[22deg] text-slate-300/[0.045] select-none pointer-events-none"
            viewBox="0 0 100 100"
            fill="currentColor"
          >
            <path d="M 50 15 C 46 28 14 50 14 66 C 14 78 26 84 42 78 C 39 83 34 89 26 93 L 74 93 C 66 89 61 83 58 78 C 74 84 86 78 86 66 C 86 50 54 28 50 15 Z" />
          </svg>

          {/* Two-Tiered Crown Silhouette (Upper Right - Facing Left, Wider, Gradient Fill, Polished) */}
          <svg
            className="absolute top-0.5 right-1 w-22 h-20 sm:w-25 sm:h-22 -rotate-[26deg] select-none pointer-events-none"
            viewBox="0 0 100 100"
          >
            <defs>
              <linearGradient id="crownWatermarkGradNoTrump" x1="20%" y1="0%" x2="80%" y2="100%">
                <stop offset="0%" stopColor="#fde68a" stopOpacity="0.14" />
                <stop offset="45%" stopColor="#f59e0b" stopOpacity="0.08" />
                <stop offset="100%" stopColor="#b45309" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {/* Bottom tier (base band) */}
            <path
              fill="url(#crownWatermarkGradNoTrump)"
              d="M 14 83 Q 50 86 86 83 L 86 91 Q 50 94 14 91 Z"
            />
            {/* Top tier (main crown with rounded outer prongs, wider stance, and diamond-topped center prong) */}
            <path
              fill="url(#crownWatermarkGradNoTrump)"
              d="M 16 76 L 84 76 L 90 36 C 92 30 82 28 78 34 C 68 44 64 48 57 46 L 57 28 L 66 18 L 50 5 L 34 18 L 43 28 L 43 46 C 36 48 32 44 22 34 C 18 28 8 30 10 36 Z"
            />
          </svg>
        </div>


        {/* SECTION 1: TITLE & SUBTITLE WITH INTEGRATED SEPARATORS */}
        <div className="flex flex-col items-center justify-center text-center relative z-10 w-full pt-1">
          <div className="flex items-center justify-center gap-3 w-full px-1">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-amber-400/70" />
            <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight shrink-0">
              <span className="sr-only">Declare Direction</span>
              <span aria-hidden="true">
                <span className="text-[#f5c544]">Declare</span> <span className="text-white">Direction</span>
              </span>
            </h2>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-amber-400/40 to-amber-400/70" />
          </div>
          <p className="text-[11px] sm:text-xs text-slate-300 font-medium tracking-wide pt-0.5">
            Bid Won • {winningBid} No-Trump
          </p>
        </div>

        {/* SEPARATOR LINE ABOVE DIRECTION SEGMENTED CONTROL */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent my-0.5 relative z-10" />

        {/* SEGMENTED CONTROL FOR DIRECTION (UPTOWN / DOWNTOWN) */}
        <div className="p-1 rounded-xl bg-[#091220]/80 border border-white/15 shadow-inner grid grid-cols-2 gap-1 relative z-10">
          {DIRECTIONS.map((dirOpt, idx) => {
            const active = selectedDirection === dirOpt.id;
            const prevActive = idx > 0 && selectedDirection === DIRECTIONS[idx - 1].id;
            return (
              <button
                key={dirOpt.id}
                type="button"
                disabled={isSubmitting}
                onClick={() => handleSelectDirection(dirOpt.id)}
                className={`relative h-10.5 sm:h-11.5 px-2 rounded-lg text-center transition-all flex items-center justify-center select-none ${
                  active
                    ? 'text-slate-950 font-black'
                    : 'text-white/80 hover:text-white hover:bg-white/5 font-bold cursor-pointer'
                }`}
              >
                {idx > 0 && !active && !prevActive && (
                  <div className="absolute -left-[2.5px] top-2 bottom-2 w-px bg-white/15 pointer-events-none" />
                )}
                {active && (
                  <motion.div
                    layoutId="activeNoTrumpDirection"
                    className="absolute inset-0 bg-gradient-to-b from-[#ffd152] via-[#ffb923] to-[#f59e0b] rounded-lg shadow-[0_0_14px_rgba(245,158,11,0.45)] border border-amber-200/60"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10 flex items-center justify-center gap-1.5 truncate">
                  {dirOpt.id === 'UP' ? (
                    <ArrowUp
                      size={15}
                      className={`stroke-[3.5] shrink-0 ${active ? 'text-emerald-700' : 'text-emerald-400'}`}
                    />
                  ) : (
                    <ArrowDown
                      size={15}
                      className={`stroke-[3.5] shrink-0 ${active ? 'text-rose-700' : 'text-rose-500'}`}
                    />
                  )}
                  <span className="text-xs sm:text-sm font-black uppercase tracking-wider truncate">
                    {dirOpt.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {/* DECLARE BUTTON */}
        <motion.button
          type="button"
          whileHover={selectedDirection && !isSubmitting ? { scale: 1.015 } : undefined}
          whileTap={selectedDirection && !isSubmitting ? { scale: 0.97 } : undefined}
          disabled={!selectedDirection || isSubmitting}
          onClick={handleDeclare}
          className={`w-full h-10.5 sm:h-11 px-3 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all border relative z-10 ${
            selectedDirection && !isSubmitting
              ? 'bg-gradient-to-r from-[#ffd152] via-[#ffb923] to-[#f59e0b] hover:from-[#ffe072] hover:to-[#ffb923] text-slate-950 border-amber-200/70 shadow-[0_4px_16px_rgba(245,158,11,0.45)] cursor-pointer'
              : 'bg-white/[0.04] text-white/25 border-white/10 cursor-not-allowed shadow-none'
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={17} className="animate-spin text-slate-950 shrink-0" />
              <span>Declaring...</span>
            </>
          ) : (
            <>
              <Check size={17} className="stroke-[3] shrink-0" />
              <span>
                {selectedDirObj
                  ? `Declare ${selectedDirObj.label}`
                  : 'Select Direction'}
              </span>
            </>
          )}
        </motion.button>

        {/* DECORATIVE FOOTER DIVIDER: ─── ♠ ─── (Larger Gold Spade) */}
        <div className="flex items-center justify-center gap-3 w-full pt-1 pb-0.5 select-none relative z-10">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400/30 to-amber-400/50" />
          <span className="text-amber-400 text-base sm:text-lg select-none leading-none drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]">♠</span>
          <div className="flex-1 h-px bg-gradient-to-l from-transparent via-amber-400/30 to-amber-400/50" />
        </div>
      </div>
    </motion.div>
  );
};
